import { Box, CircularProgress, Divider, Paper } from "@mui/material";
import Typography from "./base/typography";
import React, { useContext, useEffect, useRef, useState } from "react";
import ELineChart from "./_elements/eline-chart";
import EPieChart from "./_elements/epiechart";
import Exporter from "./_elements/exporter";
import UserContext from "../services/user/user";
import { eColors } from "../global_vars";
import EInfoToolTip from "./_elements/eInfo-tooltip";
import APIContext from "../services/api/api";
import { Grid } from "@mui/material";

import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Slide from "@mui/material/Slide";

import messages_de from "../translations/de.json";
import messages_en from "../translations/en.json";
import messages_es from "../translations/es.json";
import messages_fr from "../translations/fr.json";

import { URLS } from "../configuration/urls";

import { EcUserDataContext } from "@eclear-ag/ec-common-ui";

import { FormattedMessage } from "react-intl";

import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

export default function Revenue() {
  const { currency, formatNumber } = useContext(UserContext);
  const { userData } = useContext(EcUserDataContext);
  const { getData } = useContext(APIContext);

  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.between("xs", "md"));

  const chartsRef = useRef();

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  const [pieData, setPieData] = useState();
  const [barData, setBarData] = useState();
  const [isPieLoading, setIsPieLoading] = useState(true);
  const [isBarLoading, setIsBarLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isFetchError, setIsFetchError] = useState(false);

  const [csvData, setCsvData] = useState(null);

  const messages = {
    de: messages_de,
    en: messages_en,
    es: messages_es,
    fr: messages_fr
  };
  const { lang } = useContext(UserContext);

  function formatBarChartData(data) {
    const newData = [
      {
        name: messages[lang]["invoices"],
        data: []
      },
      {
        name: messages[lang]["creditNotes"],
        data: []
      }
    ];
    data.forEach((item) => {
      newData[0]["data"].push(Math.abs(item.total_revenue));
      newData[1]["data"].push(item.total_credits);
    });

    return newData;
  }

  function formatPieChartData(data) {
    return {
      total: data.effective_revenue,
      chartDate: [
        {
          name: messages[lang]["invoice"],
          y: data.total_revenue
        },
        {
          name: messages[lang]["creditNotes"],
          y: data.total_credits
        }
      ]
    };
  }

  function TransitionDown(props) {
    return <Slide {...props} direction="down" />;
  }

  useEffect(() => {
    async function fetchBarData() {
      let barData = await getData(`${URLS.LAST_30_DAYS}${userData.companyId}`);
      // `${URLS.LAST_30_DAYS}${userData.companyDetails?.[0].merchantIdLong}`

      if (barData === 504 || barData === 404 || barData === 500) {
        setIsEmpty(true);
        setIsBarLoading(false);
        setIsPieLoading(false);
        setIsFetchError(true);
      }

      const formatedCSVData = [];

      for (let i = 0; i < barData.current_year_reports.length; i++) {
        formatedCSVData.push({
          curr: barData.current_year_reports[i],
          prev: barData.previous_year_reports[i]
        });
      }
      formatedCSVData.push({
        effective_revenue: barData.effective_revenue,
        total_revenue: barData.total_revenue,
        total_credits: barData.total_credits
      });

      // console.log("bahi", formatedCSVData, barData);
      setCsvData(formatedCSVData);

      let pieDataRes = barData ? formatPieChartData(barData) : undefined;

      typeof pieDataRes.total === "undefined"
        ? setIsEmpty(true)
        : setIsEmpty(false);

      pieDataRes?.chartDate.forEach((item, index) => {
        item.y = Math.abs(item.y);
        item.color = isEmpty ? "#ffffff" : !isEmpty && eColors[index];
      });

      setPieData(pieDataRes);

      let barDataRes = [];
      let barDataLastRes = [];

      barDataRes = barData.current_year_reports;
      barDataLastRes = barData.previous_year_reports;

      barDataRes = barDataRes ? formatBarChartData(barDataRes) : undefined;
      barDataLastRes = barDataLastRes
        ? formatBarChartData(barDataLastRes)
        : undefined;

      barDataRes?.forEach((item, index) => {
        item.color = isEmpty
          ? "#ffffff"
          : !isEmpty && index === 0
            ? "#416290" // --future-tax-future-tax-80 (Invoices)
            : "#fab732"; // --warning-warning-80 (Credit notes)
        item.borderRadiusTopLeft = index === 0 && "50%";
        item.borderRadiusTopRight = index === 0 && "50%";
        item.borderRadiusBottomLeft = index === 1 && "50%";
        item.borderRadiusBottomRight = index === 1 && "50%";
        item.data = item.data.map((x) => {
          return typeof x === "undefined" ? 0 : x;
        });
      });

      barDataLastRes?.forEach((item, index) => {
        item.name = messages[lang]["lastYear"] + " " + item.name;
        item.color = isEmpty
          ? "#ffffff"
          : !isEmpty && index === 0
            ? "#b0b0b0"
            : "#fde68a";
        item.type = "area";
        item.zIndex = 1;
        item.fillColor = index === 0 ? "#b0b0b0" : "#fde68a";
        item.data = item.data.map((x) => {
          return typeof x === "undefined" ? 0 : x;
        });
      });

      const data =
        barDataRes && barDataLastRes
          ? {
              current: barDataRes,
              last: barDataLastRes
            }
          : undefined;
      setBarData(data);

      setIsBarLoading(false);
      setIsPieLoading(false);
    }

    if (userData.companyId) {
      fetchBarData();
    }
  }, [userData]);

  return (
    <Paper
      sx={{
        borderRadius: "8px"
      }}
      className={`widget-col-12 card-main ${isEmpty && "no-data"}`}
      elevation={0}
    >
      <Box
        sx={{
          p: "10px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Typography
          className="card-title"
          component="h2"
          data-testid="revenue_credit_notes_heading"
          sx={{
            fontSize: "20px",
            lineHeight: "28px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            color: isEmpty && "var(--font-medium-default)"
          }}
        >
          <FormattedMessage id="revenue" />
          <div style={{ textAlign: "right" }}>
            <EInfoToolTip
              opacity={1}
              darkModeSupport
              status={isEmpty ? "noData" : ""}
              data-testid="revenue_credit_notes_tooltip"
              tooltipContentTestId="revenue_credit_notes_tooltip_content"
              msg={
                <ul className="tooltip-list">
                  <li style={{ color: eColors[0] }}>
                    <FormattedMessage id="barChart.netSales" />
                  </li>
                  <li style={{ color: eColors[1] }}>
                    <FormattedMessage id="barChart.creditNotes" />
                  </li>
                </ul>
              }
            />
          </div>
        </Typography>

        {isBarLoading && isPieLoading ? (
          ""
        ) : (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* <IconButton disabled={isEmpty}>
              <img
                src={FilterIcon}
                style={{
                  width: "18px",
                  height: "18px",
                  objectFit: "contain",
                  opacity: isEmpty && 0.2,
                }}
              />
            </IconButton> */}
            <Exporter
              isEmpty={isEmpty}
              exportCSV={false}
              fileName="revenue"
              refVal={chartsRef}
              csvLink="export/dashboard/charts/bar-charts/renvue/invoice-vs-creditnotes"
              data={csvData}
              headers={[
                { label: "Current Day", key: "curr.day" },
                { label: "Current Month", key: "curr.month" },
                { label: "Current Year", key: "curr.year" },
                { label: "Current Year Invoices", key: "curr.total_revenue" },
                { label: "Current Year Credits", key: "curr.total_credits" },
                { label: "Previous Day", key: "prev.day" },
                { label: "Previous Month", key: "prev.month" },
                { label: "Previous Year", key: "prev.year" },
                { label: "Previous Year Invoices", key: "prev.total_revenue" },
                { label: "Previous Year Credits", key: "prev.total_credits" },
                { label: "Effective Revenue", key: "effective_revenue" },
                { label: "Total Revenue", key: "total_revenue" },
                { label: "Total Credit Notes", key: "total_credits" }
              ]}
              downloadButtonTestId="revenue_credit_export_button"
              pngButtonTestId="revenue_credit_png_button"
            />
          </Box>
        )}
      </Box>
      <Divider sx={{ borderWidth: "1px" }} />
      <div
        style={{
          borderRadius: "8px",
          overflow:"hidden"
        }}
        className="revenue-vs-creditNotes"
      >
        <Grid
          ref={chartsRef}
          container
          direction="row"
          justify="space-between"
          spacing={0}
          style={{ backgroundColor: "var(--app-background-1)",
            padding: "20px",
            height: "360px"
           }}
        >
          <Grid item xs={12} sm={12} md={9} order={{ xs: 2, sm: 2, md: 1 }}>
            <div
              style={{
                // flex: "3",
                // maxWidth: "100%",
                textAlign: "center"
                // pr: 3,
              }}
              data-testid="last_30_days_box"
            >
              <span
                style={{ fontSize: "14px", fontWeight: 600, float: "left" }}
              >
                <span
                  className="text"
                  data-testid="revenue-last-30-days-title"
                  // style={{ color: isEmpty && "#b3bac5" }}
                >
                  <FormattedMessage id="last30Days" />
                </span>
                <EInfoToolTip
                  darkModeSupport
                  status={isEmpty ? "noData" : ""}
                  data-testid="revenue-last-30-days-tooltip"
                  tooltipContentTestId="revenue-last-30-days-tooltip-content"
                  msg={
                    <ul className="tooltip-list">
                      <li style={{ color: eColors[0] }}>
                        <FormattedMessage id="barChart.netSales" />
                      </li>
                      <li style={{ color: eColors[1] }}>
                        <FormattedMessage id="barChart.creditNotes" />
                      </li>
                    </ul>
                  }
                  iconSize={"14px"}
                />
              </span>
              <br />
              <br />
              {isBarLoading ? (
                <CircularProgress />
              ) : (
                <React.Fragment>
                  {barData ? (
                    <ELineChart
                      currentData={barData.current}
                      lastData={barData.last}
                      isEmpty={isEmpty}
                    />
                  ) : (
                    <span
                      style={{
                        color: "var(--font-medium-default)",
                        fontWeight: "500",
                        fontSize: "24px"
                      }}
                      data-testid="revenue-bar-chart-no-data"
                    >
                      <FormattedMessage id="noData" />
                    </span>
                  )}
                </React.Fragment>
              )}
            </div>
          </Grid>
          {!isXsScreen && (
            <Grid
              item
              xs={0.5}
              sm={0.5}
              md={0.5}
              order={{ xs: 2, sm: 2 }}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <Divider
                orientation="vertical"
                sx={{ borderWidth: 0, borderRightWidth: "1px", height: "60%" }}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={12} md={2.5} order={{ xs: 1, sm: 1, md: 3 }}>
            <div
              style={{
                textAlign: "center"
              }}
              data-testid="aggregated_last_30_days_box"
            >
              <span style={{ fontSize: "14px", fontWeight: 600 }}>
                <span
                  // style={{ color: isEmpty && "#b3bac5" }}
                  className="text"
                  data-testid="aggregated_last_30_days_heading"
                >
                  <FormattedMessage id="aggregatedLast30Days" />
                </span>
                <EInfoToolTip
                  darkModeSupport
                  status={isEmpty ? "noData" : ""}
                  data-testid="aggregated_last_30_days_tooltip"
                  tooltipContentTestId="aggregated_last_30_days_tooltip_content"
                  msg={
                    <ul className="tooltip-list">
                      <li style={{ color: eColors[0] }}>
                        <FormattedMessage id="pieChart.aggregatedNet" />
                      </li>
                      <li style={{ color: eColors[1] }}>
                        <FormattedMessage id="pieChart.aggregatedCreditNotes" />
                      </li>
                    </ul>
                  }
                  iconSize={"14px"}
                />
              </span>
              <br />
              <br />
              {isPieLoading ? (
                <CircularProgress />
              ) : (
                <React.Fragment>
                  {pieData ? (
                    <EPieChart
                      centerTitle={
                        formatNumber(pieData.total, 0) + currency.symbol
                      }
                      effectiveRevenue={pieData.total}
                      title={messages[lang]["effectiveRevenue"]}
                      data={pieData.chartDate}
                      isEmpty={isEmpty}
                    />
                  ) : (
                    <span
                      className="no-data"
                      style={{
                        fontWeight: "500",
                        fontSize: "24px"
                      }}
                      data-testid="revenue-pie-chart-no-data"
                    >
                      {" "}
                      <FormattedMessage id="noData" />
                    </span>
                  )}
                </React.Fragment>
              )}
            </div>
          </Grid>
        </Grid>
      </div>

      <Snackbar
        open={isFetchError}
        autoHideDuration={6000}
        onClose={() => setIsFetchError(false)}
        TransitionComponent={TransitionDown}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
      >
        <Alert severity={"error"} sx={{ width: "100%" }}>
          <FormattedMessage id="revenue.error" />
        </Alert>
      </Snackbar>
    </Paper>
  );
}
